import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import {Contract} from '@modules/husleie-framework/src/types/Contract.entity';
import type {PrivateLandlord} from '@modules/husleie-framework/src/types/PrivateLandlord.entity';
import type {ProfessionalLandlord} from '@modules/husleie-framework/src/types/ProfessionalLandlord.entity';
import type {RentalObject} from '@modules/husleie-framework/src/types/RentalObject.entity';
import {Session} from '@modules/husleie-framework/src/types/Session.entity';
import {UserRoleEnum} from '@modules/husleie-framework/src/types/User.entity';
import ContractConversionOperations from '@modules/store/contract/static-classes/ContractConversionOperations';
import LandlordConversionOperations from '@modules/store/landlord/static-classes/LandlordConversionOperations';
import type {
  LandlordAPIInterface,
  LandlordCreate,
  LandlordCreateResponse,
  LandlordProfileAddressGet,
  LandlordProfileAddressGetResponse,
  LandlordProfileUpdate,
  LandlordRentalObjectsGet,
  LandlordRentalObjectsGetResponse,
  LandlordContractsGet,
  LandlordContractsGetResponse,
  LandlordGet,
  LandlordGetResponse,
  LandlordContractDelete,
  LandlordContractDeleteResponse,
  LandlordInspectionReportsGet,
  LandlordInspectionReportsGetResponse,
  LandlordTenanciesGet,
  LandlordTenanciesGetResponse,
  LandlordTenancyGet,
  LandlordTenancyGetResponse,
} from '@modules/store/landlord/types/LandlordAPI.interface';
import {
  LandlordInspectionReportDelete,
  LandlordInspectionReportDeleteResponse,
  LandlordDownloadSessionGet,
  LandlordDownloadSessionGetResponse,
  LandlordDownloadTokenGet,
  LandlordDownloadTokenGetResponse,
  LandlordTenancySubscriptionsGet,
  LandlordTenancySubscriptionsGetResponse,
  LandlordTenancySubscriptionsUpdate,
  LandlordTenancyDocumentsGet,
  LandlordTenancyDocumentsGetResponse,
} from '@modules/store/landlord/types/LandlordAPI.interface';
import RentalObjectConversionOperations from '@modules/store/rentalObject/static-classes/RentalObjectConversionOperations';
import {RentalObjectAPIProtocolVersionEnum} from '@modules/store/rentalObject/types/RentalObjectAPI.interface';
import SessionConversionOperations from '@modules/store/session/static-classes/SessionConversionOperations';
import {
  ProfilesUserApi,
  ProfilesLandlordApi,
  RentalObjectApi,
  UserApi,
  ContractApi,
  SessionStoreApi,
  TenancyApi,
} from '@modules/smartmieteapi';
import {Tenancy} from '@modules/husleie-framework/src/types/Tenancy.entity';
import TenancyConversionOperations from '@modules/store/tenancy/static-classes/TenancyConversionOperations';
import {PaymentMethodEnum} from '@modules/husleie-framework/src/types/Payment.entity';

const type = 'inspection_report';

interface getProfileLandlordResponseInterface {
  addresses: [
    {
      careOf: string;
      countryCode: string;
      email: string;
      postalArea: string;
      postalZipcode: string;
      purpose: string;
      street: string;
      street2: string;
    }
  ],
  id: string;
  meta: {
    needsAcknowledgement: {}
  },
  name: string;
  nationalIdentityNumber: any;
  owner: {
    id: number;
    name: string;
  },
  stats: {
    rentalObjectCount: number;
    tenancyCount: number;
  }
}

interface PatchProfileResponse  {
  addresses:[];
  id:number;
  meta:{
    needsAcknowledgement:{}
  },
  name:string;
  nationalIdentityNumber:number;
  owner:{
    id:number;
    name:string;
  },
  stats:{
    rentalObjectCount:number;
    tenancyCount:number
  }
}

export default class API implements LandlordAPIInterface {

  createLandlord(create: LandlordCreate): Promise<LandlordCreateResponse> {
    if (create.landlord) {
      return new Promise((resolve, reject) => {
        const api = new ProfilesLandlordApi();
        const body: any = LandlordConversionOperations.createLandlordInternalToApi(create.landlord);
        api
          .createProfileLandlord({ body })
          .then((apiLandlord: any) => {
            resolve({ landlord: { ...create.landlord, id: apiLandlord.id } });
          })
          .catch((error: any) => {
            LogOperations.error('API ProfilesLandlordApi:createProfileLandlord', error);
            reject(error);
          });
      });
    }
    return Promise.reject('Landlord is NULL');
  }

  getLandlord(get: LandlordGet): Promise<LandlordGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProfilesUserApi();
      api
        .getProfileUserWhoAmI()
        .then((apiLandlord: any) => {
          const landlord: PrivateLandlord | ProfessionalLandlord = LandlordConversionOperations.apiToInternal(
            apiLandlord,
            get.userTranslationOptions
          );
          resolve({ landlord, isSudo: apiLandlord.isSudo, firstRentalObjectId: apiLandlord.firstRentalObjectId });
        })
        .catch((error: any) => {
          LogOperations.error('API ProfilesLandlordApi:getLandlord', error);
          reject(error);
        });
    });
  }

  /**
   * Get landlord postal address
   *
   * @param get LandlordProfileAddressGet
   * @returns
   */
  async getProfileLandlordPostalAddress(get: LandlordProfileAddressGet): Promise<LandlordProfileAddressGetResponse> {
    try {
      const api = new ProfilesLandlordApi();
      const response: getProfileLandlordResponseInterface = await api.getProfileLandlord(get.landlord.id!);
      const address: any = response.addresses.find((address: any) => address.purpose === 'postal');
      return { address: LandlordConversionOperations.apiPostalAddressToStatePostalAddress(address) };
    } catch (error: any) {
      LogOperations.error('API ProfilesLandlordApi:getProfileLandlordPostalAddress', error);
      throw new Error(error);
    }
  }

  /**
   * Update landlord postal address
   *
   * @param update LandlordProfileUpdate
   * @returns Promise<void>
   */
  async patchProfileLandlord(update: LandlordProfileUpdate): Promise<void> {
    try {
      const api = new ProfilesLandlordApi();
      const convertedAddresses: any = LandlordConversionOperations.statePostalAddressToApiPostalAddress(update.landlord.address);

      const body: any = {
        addresses: convertedAddresses,
      };

      const response: PatchProfileResponse = await api.patchProfileLandlord(update.landlord.id, { body });
    } catch (error: any) {
      LogOperations.error('API ProfilesLandlordApi:patchProfileLandlord', error);
      throw new Error(error);
    }
  }

  getLandlordRentalObjects(get: LandlordRentalObjectsGet): Promise<LandlordRentalObjectsGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new RentalObjectApi();
      api
        .getLandlordRentalObjects(get.id)
        .then((apiRentalObjects: any) => {
          const rentalObjects: RentalObject[] = [];
          if (apiRentalObjects) {
            apiRentalObjects.map((apiRentalObject: any) => {
              rentalObjects.push(RentalObjectConversionOperations.apiToInternal(
                apiRentalObject,
                { rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v1_0_0 }));
            });
          }
          resolve({ rentalObjects });
        })
        .catch((error: any) => {
          LogOperations.error('API RentalObjectApi:getLandlordRentalObjects', error);
          reject(error);
        });
    });
  }

  getLandlordContracts(get: LandlordContractsGet): Promise<LandlordContractsGetResponse> {
    return new Promise((resolve, reject) => {
      let query: any = undefined;
      if (get.status) {
        query = {
          status: get.status,
        };
      }
      const api = new UserApi();
      api
        .getUserContracts({ query })
        .then((apiContracts: any) => {
          const contracts: Contract[] = [];
          if (apiContracts) {
            apiContracts.map((apiContract: any) => {
              const apiRentalObject: any = apiContract.rentalObject ?
                { ...apiContract.rentalObject, id: apiContract.rentalObjectId } :
                { id: apiContract.rentalObjectId };
              const apiContractToConvert: any = { ...apiContract, uuid: apiContract.id, rentalObject: apiRentalObject };
              contracts.push(
                ContractConversionOperations.apiToInternal(
                  apiContractToConvert,
                  get.userTranslationOptions,
                  { rentalObjectAPIProtocolVersion: RentalObjectAPIProtocolVersionEnum.v2_0_0 })
              );
            });
          }
          resolve({ contracts });
        })
        .catch((error: any) => {
          LogOperations.error('API UserApi:getUserContracts', error);
          reject(error);
        });
    });
  }

  getLandlordInspectionReports(get: LandlordInspectionReportsGet): Promise<LandlordInspectionReportsGetResponse> {
    return new Promise((resolve, reject) => {
      let query: any = undefined;
      if (get.status) {
        query = {
          status: get.status,
        };
      }

      const api = new UserApi();
      api
        .getUserSessions('inspection_report',{ query })
        .then((apiInspectionReports: any) => {
          const inspectionReports: Session[] = [];
          if (apiInspectionReports) {
            apiInspectionReports.map((apiInspectionReport: any) => {
              inspectionReports.push(
                SessionConversionOperations.apiToInternal(
                  get.type,
                  {
                    ...apiInspectionReport.object,
                    uuid: apiInspectionReport.id,
                    tenancyId: apiInspectionReport.tenancyId,
                    signingUuid: apiInspectionReport.signingId,
                    signerUuid: apiInspectionReport.landlordSignerId,
                    signerRole: UserRoleEnum.LANDLORD,
                    status: apiInspectionReport.status,
                    created:apiInspectionReport.created,
                    modified: apiInspectionReport.modified
                  },
                  get.userTranslationOptions)
              );
            });
          }
          resolve({ inspectionReports });
        })
        .catch((error: any) => {
          LogOperations.error('API UserApi:getUserSessions', error);
          reject(error);
        });
    });
  }

  getLandlordTenancy(get: LandlordTenancyGet): Promise<LandlordTenancyGetResponse> {
    return new Promise((resolve, reject) => {

      const api = new TenancyApi();
      api
        .getLandlordTenancy(get.id, get.tenancyId)
        .then((data: any) => {
          const tenancy: Tenancy = TenancyConversionOperations.apiToInternal(data, get.userTranslationOptions);
          resolve({ tenancy });
        })
        .catch((error: any) => {
          LogOperations.error('API TenancyApi:getLandlordTenancies', error);
          reject(error);
        });
    });
  }

  getLandlordTenancies(get: LandlordTenanciesGet): Promise<LandlordTenanciesGetResponse> {
    return new Promise((resolve, reject) => {

      const api = new TenancyApi();
      api
        .getLandlordTenancies(get.id)
        .then((apiTenancies: any) => {
          const tenancies: Tenancy[] = [];
          apiTenancies.map((data: any) => {
            const tenancy: Tenancy = TenancyConversionOperations.apiToInternal(data, get.userTranslationOptions);
            tenancies.push(tenancy);
          });
          resolve({ tenancies });
        })
        .catch((error: any) => {
          LogOperations.error('API LandlordApi:getLandlordTenancies', error);
          reject(error);
        });
    });
  }

  getLandlordTenancySubscriptions(
    get: LandlordTenancySubscriptionsGet
  ): Promise<LandlordTenancySubscriptionsGetResponse> {
    return new Promise((resolve, reject) => {

      const api = new TenancyApi();
      api
        .getTenancySubscriptionPlans(get.id, get.tenancyId)
        .then((data: any) => {
          resolve({ subscriptionPlans: data.subscriptionPlans, payment: data.payment });
        })
        .catch((error: any) => {
          LogOperations.error('API LandlordApi:getLandlordTenancySubscriptions', error);
          reject(error);
        });
    });
  }

  updateLandlordTenancySubscription(update: LandlordTenancySubscriptionsUpdate): Promise<void> {
    return new Promise((resolve, reject) => {
      const api = new TenancyApi();

      let payment: any;
      if (update.payment?.methodType === PaymentMethodEnum.STRIPE) {
        payment = {
          type: 'stripe',
          stripe: {
            paymentMethodId: update.payment.methodId!
          }
        }
      }

      api
        .updateTenancySubscription(
          update.id,
          update.tenancyId,
          { body: { subscriptionPlanId: update.subscriptionPlanId, payment, origin: update.origin || '' } }
        )
        .then(() => resolve())
        .catch((error: any) => {
          LogOperations.error('API LandlordApi:updateLandlordTenancySubscription', error);
          reject(error);
        });
    });
  }

  deleteLandlordContract(remove: LandlordContractDelete): Promise<LandlordContractDeleteResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      api
        .deleteContractSession(remove.uuid)
        .then(() => {
          resolve({});
        })
        .catch((error: any) => {
          LogOperations.error('API ContractApi:deleteContractSession', error);
          reject(error);
        });
    });
  }

  deleteLandlordInspectionReport(
    remove: LandlordInspectionReportDelete
  ): Promise<LandlordInspectionReportDeleteResponse> {

    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      api
        .deleteSession('inspection_report', remove.uuid)
        .then(() => {
          resolve({});
        })
        .catch((error: any) => {
          LogOperations.error('API SessionStoreApi:deleteSession', error);
          reject(error);
        });
    });
  }

  getLandlordDownLoadToken(get: LandlordDownloadTokenGet): Promise<LandlordDownloadTokenGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      api.getDownloadTokenByJWT(type, get.uuid)
        .then((data: any) => {
          resolve({ token: data.token });
        })
        .catch((error: Error) => {
        LogOperations.error('API|getLandlordDownLoadToken', error);
        reject(error);
      });
    });
  }

  getLandlordDownLoadSession(get: LandlordDownloadSessionGet): Promise<LandlordDownloadSessionGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApi();
      const downloadQuery = { token: get.token, format: 'pdf' };
      api.downloadSession(type, get.uuid, { query: downloadQuery }, 'blob')
        .then((data: any) => {
          resolve({ pdf: data });
        })
        .catch((error: Error) => {
          LogOperations.error('API|getLandlordDownLoadSession', error);
          reject(error);
        });
    });
  }

  getLandlordTenancyDocuments(get: LandlordTenancyDocumentsGet): Promise<LandlordTenancyDocumentsGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new TenancyApi();
      api.getLandlordTenancyDocuments(get.landlordId, get.tenancyId)
        .then((data: any) => {
          resolve({ documents: data });
        })
        .catch((error: Error) => {
          LogOperations.error('API|getLandlordTenancyDocuments', error);
          reject(error);
        });
    });
  }
}
